// src/components/Dashboard.tsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API from '../config/api';
import { openPrintWindow, printHtml as triggerPrint } from './printUtils';
import { Layout, Card, Typography, Button, Space, Alert, Row, Col, Drawer, Grid } from 'antd';
import {
  SettingOutlined,
  WarningOutlined,
  ItalicOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { usePatientContext } from './PatientContext';
import { useDoctorContext } from './DoctorContext';
import { useClinicContext } from './ClinicContext';
import { Receipt } from './type';
import moment from 'moment';
import { getFirestore, collection, query, onSnapshot, doc } from 'firebase/firestore';
import { useLanguage } from './LanguageContext';
import DashboardSidebar from './DashboardSidebar';
import { useOwnerContext } from './OwnerContext';
import DashboardHeader from './DashboardHeader';
import PatientsList from './PatientsList';
import PatientDetail from './PatientDetail';
import ReceiptModal from './ReceiptPatient';
import ReceiptDetail from './ReceiptDetails';
import ClinicSelector from './ClinicSelector';
import SidebarWaitingList from './SidebarWaitingList';
import SendNotification from './SendNotification';
import AssistantNotificationsPanel from './AssistantNotificationsPanel';
import NextVisits from './NextVisits';
import DoctorAbsentModal from './DoctorAbsentModal';
import { getRxPrintArPatientFreqOnEnPrescription } from '../utils/prescriptionPrintLocalSettings';
import { getClinicOwnerDoctorId } from '../utils/clinicOwnerDoctor';

const { useBreakpoint } = Grid;
const { Content, Sider } = Layout;
const { Title, Text } = Typography;

/** Toggle to show the Doctor Absent button (next to Hide Waiting List) and modal again. */
const SHOW_DOCTOR_ABSENT_UI = false;

const Dashboard: React.FC = () => {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [isReceiptModalVisible, setIsReceiptModalVisible] = useState<boolean>(false);
  const [clinicalHistoryRefreshToken, setClinicalHistoryRefreshToken] = useState(0);
  const [waitingListVisible, setWaitingListVisible] = useState<boolean>(true);
  const [sidebarDrawerVisible, setSidebarDrawerVisible] = useState<boolean>(false);
  const [waitingListDrawerVisible, setWaitingListDrawerVisible] = useState<boolean>(false);
  const waitingListUnsubscribe = useRef<any>(null);
  // Tracks whether the *next* Firestore snapshot is the first one delivered
  // after the listener attaches. On first delivery Firestore reports every
  // existing doc as `added`, which would otherwise look like a fresh
  // "Enter to doctor" event for every already-finished patient from earlier
  // today. Reset to `true` every time we (re)attach the listener.
  const waitingListInitialSnapshotRef = useRef<boolean>(true);
  // Debounce coalesces rapid-fire Firestore writes for the same Enter
  // (e.g. status='FINISHED' followed ~50ms later by a file_number update)
  // into a single force refresh.
  const waitingListRefreshDebounceRef = useRef<any>(null);
  const waitingListRetryTimeoutsRef = useRef<any[]>([]);
  const patientDetailHistoryPushedRef = useRef<boolean>(false);
  const [waitingListRefreshTrigger, setWaitingListRefreshTrigger] = useState<number>(0);
  const sidebarWaitingListRef = useRef<any>(null);
  const [patientsNeedRefresh, setPatientsNeedRefresh] = useState<boolean>(false);
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState<number>(0);
  const lastRefreshTimestampRef = useRef<number>(0);
  const [notificationModalVisible, setNotificationModalVisible] = useState<boolean>(false);
  const [doctorAbsentModalVisible, setDoctorAbsentModalVisible] = useState<boolean>(false);
  const [visitTypes, setVisitTypes] = useState<any[]>([]);
  const [canUseOwnerPrintSettings, setCanUseOwnerPrintSettings] = useState<boolean>(false);
  const [ownerPrintSettingsSource, setOwnerPrintSettingsSource] = useState<any | null>(null);

  // Responsive breakpoints using Ant Design Grid
  const screens = useBreakpoint();
  const isMobile = !screens.md; // md breakpoint is 768px
  const isTablet = screens.md && !screens.lg; // lg breakpoint is 992px

  const navigate = useNavigate();
  const { isViewerOnly } = useOwnerContext();

  // Get data from contexts
  const { username } = useAuth();
  const {
    selectedPatient,
    setSelectedPatient,
    fetchPatients,
    clearPatientListCache
  } = usePatientContext();

  // Use language context for direction and print language (must match dashboard UI, not only doctor printLocale in DB)
  const { isRTL, language } = useLanguage();

  // Get doctor settings for receipt customization
  const { settings: doctorSettings, fetchSettings, doctorId } = useDoctorContext();
  const doctorIdForNotif = doctorId || localStorage.getItem('doctorId');

  // Appointment Slots deep-link: resolve the scheduled patient, then hand the
  // full API record to the existing PatientDetail screen. Normal dashboard
  // visits do not execute this isolated path.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const patientId = params.get('scheduled_patient_id');
    const patientPhone = params.get('scheduled_patient_phone');
    if ((!patientId && !patientPhone) || !doctorIdForNotif) return;
    let cancelled = false;
    axios.get(`${API.BASE_URL}/api/patients/search`, { params: {
      doctor_id: doctorIdForNotif,
      ...(patientId ? { patient_id: patientId } : { patient_phone: patientPhone }),
    }}).then(response => {
      if (!cancelled && response.data?.patients?.[0]) {
        window.history.replaceState(window.history.state, '', '/dashboard');
        setSelectedPatient(response.data.patients[0]);
      }
    }).catch(error => console.warn('Could not open scheduled patient profile:', error));
    return () => { cancelled = true; };
  }, [doctorIdForNotif, setSelectedPatient]);

  // Get clinic context
  const { selectedClinicId, setSelectedClinicId, clinics } = useClinicContext();
  const useOwnerPrintSettingsPreference = doctorSettings.printSettings?.useOwnerPrintSettings;

  useEffect(() => {
    let cancelled = false;

    const loadOwnerPrintSettings = async () => {
      const currentDoctorId = doctorId || localStorage.getItem('doctorId');
      setOwnerPrintSettingsSource(null);
      setCanUseOwnerPrintSettings(false);

      if (!currentDoctorId || !selectedClinicId || selectedClinicId === '__all_clinics__') {
        return;
      }

      try {
        const ownerDoctorId = await getClinicOwnerDoctorId(selectedClinicId, currentDoctorId);
        const isChildDoctor = !!ownerDoctorId && ownerDoctorId !== currentDoctorId;
        if (cancelled) return;

        setCanUseOwnerPrintSettings(isChildDoctor);
        if (!isChildDoctor || useOwnerPrintSettingsPreference === false) {
          return;
        }

        const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SETTINGS(ownerDoctorId)}`);
        if (!cancelled) {
          setOwnerPrintSettingsSource(response.data?.settings || null);
        }
      } catch (error) {
        console.warn('Failed to load owner print settings:', error);
        if (!cancelled) {
          setOwnerPrintSettingsSource(null);
        }
      }
    };

    loadOwnerPrintSettings();
    return () => {
      cancelled = true;
    };
  }, [doctorId, selectedClinicId, useOwnerPrintSettingsPreference]);

  // Give Patient Details its own browser-history step so the browser Back
  // button returns to the patient list without changing the dashboard route.
  useEffect(() => {
    if (selectedPatient && !patientDetailHistoryPushedRef.current) {
      window.history.pushState(
        { ...window.history.state, noWaitingPatientDetail: true },
        '',
        window.location.href
      );
      patientDetailHistoryPushedRef.current = true;
    } else if (!selectedPatient) {
      patientDetailHistoryPushedRef.current = false;
    }
  }, [selectedPatient]);

  useEffect(() => {
    const handleBrowserBack = () => {
      if (patientDetailHistoryPushedRef.current) {
        patientDetailHistoryPushedRef.current = false;
        setSelectedPatient(null);
      }
    };

    window.addEventListener('popstate', handleBrowserBack);
    return () => window.removeEventListener('popstate', handleBrowserBack);
  }, [setSelectedPatient]);

  const handleBackToPatientList = () => {
    if (
      patientDetailHistoryPushedRef.current &&
      window.history.state?.noWaitingPatientDetail
    ) {
      window.history.back();
      return;
    }

    patientDetailHistoryPushedRef.current = false;
    setSelectedPatient(null);
  };

  // Load data on component mount - only fetch settings once on mount
  useEffect(() => {
    console.log('Dashboard rendering with', selectedPatient?.patient_name || 'no selected patient');

    // Fetch doctor settings only once on component mount
    fetchSettings();

    // Fetch visit types for print label resolution
    const dId = doctorId || localStorage.getItem('doctorId');
    if (dId) {
      axios.get(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(dId)}`)
        .then(res => { if (res.data?.success) setVisitTypes(res.data.data.visit_types || []); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Setup Firestore listener for waiting list changes
  useEffect(() => {
    if (selectedClinicId) {
      setupWaitingListListener(selectedClinicId);
    }

    // Cleanup function to remove listener when component unmounts or clinic changes
    return () => {
      if (waitingListUnsubscribe.current) {
        waitingListUnsubscribe.current();
        waitingListUnsubscribe.current = null;
      }
      if (waitingListRefreshDebounceRef.current) {
        clearTimeout(waitingListRefreshDebounceRef.current);
        waitingListRefreshDebounceRef.current = null;
      }
      clearWaitingListRetryTimers();
    };
  }, [selectedClinicId]);

  // Auto-refresh patients list when needed - with reduced debounce for better responsiveness.
  // Skip refresh entirely while PatientDetail is open (selectedPatient != null) or a receipt
  // modal is visible — both can contain in-progress user actions (voice notes, forms, etc.)
  // that would be disrupted by a re-render caused by fetchPatients().
  useEffect(() => {
    if (patientsNeedRefresh && !isReceiptModalVisible && !selectedPatient) {
      const currentTime = Date.now();
      // Reduced delay to 1 second for better real-time experience
      if (currentTime - lastRefreshTimestamp > 1000) {
        console.log('Auto-refreshing patients list after waiting list change');
        // A waiting-list change typically follows a write (Enter to doctor,
        // status update, etc.), so bypass the short-lived response cache and
        // read fresh data from the server.
        fetchPatients({ forceRefresh: true }).then(() => {
          setPatientsNeedRefresh(false);
          setLastRefreshTimestamp(currentTime);
          lastRefreshTimestampRef.current = currentTime;
          // Bumping the trigger lets PatientsList know it can re-derive its
          // local pagination/total. The actual network round-trip is folded
          // into this one via the in-flight dedup in PatientContext.
          setWaitingListRefreshTrigger(prev => prev + 1);
        }).catch((error) => {
          console.error('Error auto-refreshing patients list:', error);
          setPatientsNeedRefresh(false);
        });
      } else {
        // If too soon, schedule a retry after the delay period
        const delay = 1000 - (currentTime - lastRefreshTimestamp);
        setTimeout(() => {
          if (patientsNeedRefresh && !isReceiptModalVisible && !selectedPatient) {
            console.log('Retrying auto-refresh after delay');
            fetchPatients({ forceRefresh: true }).then(() => {
              setPatientsNeedRefresh(false);
              setLastRefreshTimestamp(Date.now());
              setWaitingListRefreshTrigger(prev => prev + 1);
            });
          }
        }, delay);
      }
    }
  }, [patientsNeedRefresh, isReceiptModalVisible, selectedPatient, lastRefreshTimestamp, fetchPatients]);

  // Function to format date as used in Firebase
  const formatDate = (date?: Date): string => {
    const d = date || new Date();
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  };

  const clearWaitingListRetryTimers = () => {
    waitingListRetryTimeoutsRef.current.forEach(timer => clearTimeout(timer));
    waitingListRetryTimeoutsRef.current = [];
  };

  const forceRefreshPatientsAfterWaitingListChange = (reason: string) => {
    clearPatientListCache();
    console.log(`[Dashboard] ${reason} -> force refreshing patient list`);
    return fetchPatients({ forceRefresh: true })
      .then(() => {
        setPatientsNeedRefresh(false);
        const t = Date.now();
        setLastRefreshTimestamp(t);
        lastRefreshTimestampRef.current = t;
        setWaitingListRefreshTrigger(prev => prev + 1);
      })
      .catch((err) => {
        console.error(`[Dashboard] ${reason} refresh failed:`, err);
      });
  };

  const scheduleWaitingListRefreshRetries = () => {
    clearWaitingListRetryTimers();
    [2500, 7000].forEach((delay) => {
      const timer = setTimeout(() => {
        forceRefreshPatientsAfterWaitingListChange(
          `waiting-list delayed retry (${delay}ms)`
        );
      }, delay);
      waitingListRetryTimeoutsRef.current.push(timer);
    });
  };

  // Function to setup Firestore listener for waiting list changes
  const setupWaitingListListener = (clinicId: string) => {
    try {
      const db = getFirestore();
      const currentDate = formatDate();

      console.log('Setting up Firestore listener for clinic:', clinicId, 'date:', currentDate);

      // Define path variations as complete strings to avoid TypeScript spread error
      const pathVariations = [
        // Variation 1: Standard path
        `clinics/${clinicId}/waiting_list/${currentDate}/patients`,
        // Variation 2: Alternative collection name
        `clinics/${clinicId}/waitingList/${currentDate}/patients`,
        // Variation 3: Direct patients collection
        `clinics/${clinicId}/patients`
      ];

      // Remove existing listener if there is one
      if (waitingListUnsubscribe.current) {
        waitingListUnsubscribe.current();
        waitingListUnsubscribe.current = null;
      }

      // Reset before (re)attach — the very next snapshot will replay every
      // existing doc as "added" and we must not treat those as fresh
      // Enter-to-doctor events.
      waitingListInitialSnapshotRef.current = true;
      if (waitingListRefreshDebounceRef.current) {
        clearTimeout(waitingListRefreshDebounceRef.current);
        waitingListRefreshDebounceRef.current = null;
      }
      clearWaitingListRetryTimers();

      // Try each path for setting up the listener
      let listenerSet = false;

      for (const path of pathVariations) {
        try {
          console.log('Trying to set up listener on path:', path);
          // Use collection() with the path string directly
          const collRef = collection(db, path);

          // Set up new listener
          const unsubscribe = onSnapshot(collRef, (snapshot) => {
            console.log('Waiting list update detected in path:', path);
            console.log('Snapshot size:', snapshot.size, 'documents',
              '— changes:', snapshot.docChanges().length);

            // Always refresh the sidebar waiting list (cheap, Firestore-only).
            refreshWaitingListOnly();

            // Skip the very first delivered snapshot — Firestore replays
            // every existing doc as `added` on initial subscription, and
            // we don't want that initial fan-out to look like a fresh
            // patient-list event.
            if (waitingListInitialSnapshotRef.current) {
              waitingListInitialSnapshotRef.current = false;
              return;
            }

            // Any change after the initial snapshot — added, modified, or
            // removed — might mean a patient was just Entered (status →
            // FINISHED), cancelled, or otherwise had their visit row
            // mutated. We deliberately do NOT filter by status or change
            // type here: the assistant portal is a separate app (Android),
            // and depending on its flow + network conditions Firestore can
            // surface the very same Enter as `modified` (status change on
            // existing doc), `added` (cache-then-server replay), or even
            // `removed` (some flows delete the doc once finished). The
            // simplest reliable rule is: any waiting-list mutation → pull
            // a fresh patient list. The 250ms debounce coalesces the
            // back-to-back status + file_number writes the dashboard's
            // own Enter flow does.
            clearPatientListCache();

            if (waitingListRefreshDebounceRef.current) {
              clearTimeout(waitingListRefreshDebounceRef.current);
            }
            waitingListRefreshDebounceRef.current = setTimeout(() => {
              waitingListRefreshDebounceRef.current = null;
              console.log(
                '[Dashboard] waiting-list change → force refreshing patient list'
              );
              forceRefreshPatientsAfterWaitingListChange('waiting-list change')
                .finally(() => scheduleWaitingListRefreshRetries());
            }, 250);
          }, (error) => {
            console.error('Error in waiting list listener for path:', path, error);
          });

          waitingListUnsubscribe.current = unsubscribe;
          listenerSet = true;
          console.log('Firestore waiting list listener set up for path:', path);
          break;
        } catch (error) {
          console.log('Could not set up listener for path:', path, error);
        }
      }

      if (!listenerSet) {
        console.error('Failed to set up listener for any path variation');
      }
    } catch (error) {
      console.error('Error setting up Firestore listener:', error);
    }
  };

  // Function to refresh only the waiting list data
  const refreshWaitingListOnly = () => {
    console.log('Refreshing waiting list only...');

    // Method 1: If SidebarWaitingList exposes a refresh function through a ref
    if (sidebarWaitingListRef.current && sidebarWaitingListRef.current.refreshData) {
      console.log('Refreshing via ref method');
      sidebarWaitingListRef.current.refreshData();
    }
    // Method 2: Trigger a refresh through a state update
    else {
      console.log('Refreshing via trigger method');
      setWaitingListRefreshTrigger(prev => prev + 1);
    }
  };

  // Handle clinic selection
  const handleClinicSelect = (clinicId: string) => {
    setSelectedClinicId(clinicId);
    // PatientContext already re-fetches automatically when selectedClinicId changes,
    // so calling fetchPatients() here was causing a duplicate request.
  };

  // Manual refresh function
  const handleRefresh = () => {
    console.log('Manual refresh requested');
    // User clicked refresh — always bypass the FE response cache.
    fetchPatients({ forceRefresh: true });
    refreshWaitingListOnly();
    setPatientsNeedRefresh(false); // Clear the auto-refresh flag
    setLastRefreshTimestamp(Date.now());
  };

  // Navigate to settings page
  const handleSettingsClick = () => {
    navigate('/settings');
  };

  // Toggle waiting list sidebar/drawer
  const toggleWaitingList = () => {
    if (isMobile) {
      setWaitingListDrawerVisible(!waitingListDrawerVisible);
    } else {
      setWaitingListVisible(!waitingListVisible);
    }
  };

  // Update button text based on visibility
  const getWaitingListButtonText = () => {
    if (isMobile) {
      return waitingListDrawerVisible ? 'Hide Waiting List' : 'Show Waiting List';
    }
    return waitingListVisible ? 'Hide Waiting List' : 'Show Waiting List';
  };

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  // Map stored form/route values to human-readable print labels
  const formatDrugForm = (val: string, locale: string) => {
    if (!val) return '';
    const en: Record<string, string> = {
      Tab: 'Tab', Capsule: 'Capsule', Sachets: 'Sachets', Amp: 'Amp', Patch: 'Patch', Cream: 'Cream', Syrup: 'Syrup', Gel: 'Gel', EyeDrops: 'Eye drops',
      UnderTongue: 'Under the tongue',
    };
    const ar: Record<string, string> = {
      Tab: 'أقراص', Capsule: 'كبسولات', Sachets: 'أكياس', Amp: 'أمبول', Patch: 'لصقة', Cream: 'كريم', Syrup: 'شراب', Gel: 'جل', EyeDrops: 'قطرات عين',
      UnderTongue: 'تحت اللسان',
    };
    return locale === 'ar' ? (ar[val] ?? val) : (en[val] ?? val);
  };
  const formatDrugRoute = (val: string, locale: string) => {
    if (!val) return '';
    const en: Record<string, string> = {
      Iv: 'IV', Im: 'IM', Oral: 'Oral', Spray: 'Spray',
      Ointment: 'Ointment', Topical: 'Topical', Sublingual: 'Sublingual', Lingual: 'Lingual',
    };
    const ar: Record<string, string> = {
      Iv: 'وريدي', Im: 'عضلي', Oral: 'فموي', Spray: 'بخاخ',
      Ointment: 'مرهم', Topical: 'موضعي', Sublingual: 'تحت اللسان', Lingual: 'على اللسان',
    };
    return locale === 'ar' ? (ar[val] ?? val) : (en[val] ?? val);
  };

  // Helpers to translate Arabic drug values to English (for print only)
  const formatDrugTiming = (timing: string) => {
    if (!timing) return '';
    const s = String(timing).trim();
    const arToEn: Record<string, string> = {
      "بعد الأكل": "After Eating", "قبل الأكل": "Before Eating", "قبل النوم": "Before Sleeping",
      "وسط الاكل": "During Meals",
      "عند النوم": "At Bed Time", "كل 8 ساعات": "Every 8 Hours", "كل 6 ساعات": "Every 6 Hours",
      "كل 12 ساعة": "Every 12 Hours", "كل ٨ ساعات": "Every 8 Hours", "كل ٦ ساعات": "Every 6 Hours",
      "مرتين يومياً": "Twice a Day", "مرة يومياً": "Once a Day", "3 مرات يومياً": "Three Times a Day",
      "٣ مرات يومياً": "Three Times a Day", "مرة واحدة": "Once", "مره واحده": "Once",
      "عند اللزوم": "As Needed", "مع الأكل": "With Meals",
      "بعد الفطار": "After breakfast", "بعد الغداء": "After lunch", "قبل العشا": "Before dinner", "بعد العشاء": "After dinner",
    };
    return arToEn[s] || timing;
  };
  const formatDrugFrequency = (v: string) => {
    if (!v) return '';
    const s = String(v).trim();
    const arToEn: Record<string, string> = {
      "مرة واحدة": "Once", "مره واحده": "Once", "واحدة مرة": "Once", "واحده مره": "Once",
      "مرتين يومياً": "Twice a day", "مرتين": "Twice a day", "مرة يومياً": "Once a day", "مرة يوميا": "Once a day",
      "3 مرات يومياً": "3 times a day", "3 مرات": "3 times a day", "٣ مرات يومياً": "3 times a day", "٣ مرات": "3 times a day", "3 مرات يوميا": "3 times a day",
      "4 مرات يومياً": "4 times a day", "4 مرات": "4 times a day", "٤ مرات يومياً": "4 times a day", "٤ مرات": "4 times a day", "أربع مرات يومياً": "4 times a day", "أربع مرات": "4 times a day",
      "5 مرات يومياً": "5 times a day", "5 مرات": "5 times a day", "٥ مرات": "5 times a day", "خمس مرات يومياً": "5 times a day", "خمس مرات": "5 times a day",
      "6 مرات": "6 times a day", "٦ مرات": "6 times a day",
      "أسبوعياً": "Weekly",
    };
    if (arToEn[s]) return arToEn[s];
    // Fallback: "N مرات" → "N times a day"
    const timesMatch = s.match(/^([\d١٢٣٤٥٦٧٨٩٠]+)\s*مرات/);
    if (timesMatch) return timesMatch[1] + ' times a day';
    return v;
  };
  const formatDrugPeriod = (v: string) => {
    if (!v) return '';
    const s = String(v).trim();
    const arToEn: Record<string, string> = {
      "يوم": "1 day", "يوم واحد": "1 day", "1 يوم": "1 day", "١ يوم": "1 day", "1يوم": "1 day",
      "يومين": "2 days", "2 يوم": "2 days", "٢ يوم": "2 days", "3 أيام": "3 days", "٣ أيام": "3 days",
      "اسبوع": "1 week", "أسبوع": "1 week", "1 اسبوع": "1 week", "١ اسبوع": "1 week", "اسبوع واحد": "1 week",
      "اسبوعين": "2 weeks", "أسبوعين": "2 weeks", "2 اسبوع": "2 weeks", "٢ اسبوع": "2 weeks",
      "شهر": "1 month", "شهر واحد": "1 month", "1 شهر": "1 month", "شهرين": "2 months", "٢ شهر": "2 months",
    };
    if (arToEn[s]) return arToEn[s];
    const dayMatch = s.match(/^([\d١٢٣٤٥٦٧٨٩٠])\s*يوم/);
    if (dayMatch) return dayMatch[1] + " day" + (dayMatch[1] !== "1" && dayMatch[1] !== "١" ? "s" : "");
    const weekMatch = s.match(/^([\d١٢٣٤٥٦٧٨٩٠])\s*اسبوع/);
    if (weekMatch) return weekMatch[1] + " week" + (weekMatch[1] !== "1" && weekMatch[1] !== "١" ? "s" : "");
    const monthMatch = s.match(/^([\d١٢٣٤٥٦٧٨٩٠])\s*شهر/);
    if (monthMatch) return monthMatch[1] + " month" + (monthMatch[1] !== "1" && monthMatch[1] !== "١" ? "s" : "");
    return v;
  };

  const formatDrugDoseQty = (v: string) => {
    if (!v) return '';
    const s = String(v).trim();
    const arToEn: Record<string, string> = {
      قرص: '1 tablet',
      قرصين: '2 tablets',
      '3 أقراص': '3 tablets',
      '4 أقراص': '4 tablets',
      '1': '1 tablet',
      '1.5': '1.5',
      '2': '2 tablets',
      '2.5': '2.5',
      '3': '3 tablets',
      '4': '4 tablets',
    };
    return arToEn[s] || v;
  };

  const hasPrintableDrugValue = (value: any): boolean => {
    const text = String(value ?? '').trim();
    return Boolean(text && text !== '_');
  };

  const capitalizeDrugNameForPrint = (value: any): string => {
    const text = String(value ?? '');
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
  };

  const escapePrintText = (value: any): string => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const splitDrugFreeTextForPrint = (value: any): { title: string; descriptionHtml: string } => {
    const lines = String(value ?? '')
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);

    if (lines.length === 0) return { title: '', descriptionHtml: '' };

    return {
      title: capitalizeDrugNameForPrint(escapePrintText(lines[0])),
      descriptionHtml: lines.slice(1).map(escapePrintText).join('<br/>')
    };
  };

  const joinDrugPrintParts = (parts: string[]): string => parts.filter(Boolean).join(' | ');

  /** Hybrid EN print: drug detail line fully in Arabic (labels + values). */
  const drugFieldToArabicPrint = (
    raw: string,
    arToEn: Record<string, string>,
    toEnglish: (v: string) => string
  ): string => {
    const s = String(raw || '').trim();
    if (!s) return '';
    if (Object.prototype.hasOwnProperty.call(arToEn, s)) return s;
    const computedEn = toEnglish(s);
    for (const [ar, en] of Object.entries(arToEn)) {
      if (en === computedEn) return ar;
    }
    for (const [ar, en] of Object.entries(arToEn)) {
      if (en === s) return ar;
    }
    return s;
  };

  const hybridArabicDrugDetailsLine = (drug: any): string => {
    const freqArToEn: Record<string, string> = {
      "مرة واحدة": "Once", "مره واحده": "Once", "واحدة مرة": "Once", "واحده مره": "Once",
      "مرتين يومياً": "Twice a day", "مرتين": "Twice a day", "مرة يومياً": "Once a day", "مرة يوميا": "Once a day",
      "3 مرات يومياً": "3 times a day", "3 مرات": "3 times a day", "٣ مرات يومياً": "3 times a day", "٣ مرات": "3 times a day", "3 مرات يوميا": "3 times a day",
      "4 مرات يومياً": "4 times a day", "4 مرات": "4 times a day", "٤ مرات يومياً": "4 times a day", "٤ مرات": "4 times a day", "أربع مرات يومياً": "4 times a day", "أربع مرات": "4 times a day",
      "5 مرات يومياً": "5 times a day", "5 مرات": "5 times a day", "٥ مرات": "5 times a day", "خمس مرات يومياً": "5 times a day", "خمس مرات": "5 times a day",
      "6 مرات": "6 times a day", "٦ مرات": "6 times a day",
      "أسبوعياً": "Weekly",
    };
    const periodArToEn: Record<string, string> = {
      "يوم": "1 day", "يوم واحد": "1 day", "1 يوم": "1 day", "١ يوم": "1 day", "1يوم": "1 day",
      "يومين": "2 days", "2 يوم": "2 days", "٢ يوم": "2 days", "3 أيام": "3 days", "٣ أيام": "3 days",
      "اسبوع": "1 week", "أسبوع": "1 week", "1 اسبوع": "1 week", "١ اسبوع": "1 week", "اسبوع واحد": "1 week",
      "اسبوعين": "2 weeks", "أسبوعين": "2 weeks", "2 اسبوع": "2 weeks", "٢ اسبوع": "2 weeks",
      "شهر": "1 month", "شهر واحد": "1 month", "1 شهر": "1 month", "شهرين": "2 months", "٢ شهر": "2 months",
    };
    const timingArToEn: Record<string, string> = {
      "بعد الأكل": "After Eating", "قبل الأكل": "Before Eating", "قبل النوم": "Before Sleeping",
      "وسط الاكل": "During Meals",
      "عند النوم": "At Bed Time", "كل 8 ساعات": "Every 8 Hours", "كل 6 ساعات": "Every 6 Hours",
      "كل 12 ساعة": "Every 12 Hours", "كل ٨ ساعات": "Every 8 Hours", "كل ٦ ساعات": "Every 6 Hours",
      "مرتين يومياً": "Twice a Day", "مرة يومياً": "Once a Day", "3 مرات يومياً": "Three Times a Day",
      "٣ مرات يومياً": "Three Times a Day", "مرة واحدة": "Once", "مره واحده": "Once",
      "عند اللزوم": "As Needed", "مع الأكل": "With Meals",
      "بعد الفطار": "After breakfast", "بعد الغداء": "After lunch", "قبل العشا": "Before dinner", "بعد العشاء": "After dinner",
    };
    const freq = drugFieldToArabicPrint(drug.frequency, freqArToEn, formatDrugFrequency);
    const per = drugFieldToArabicPrint(drug.period, periodArToEn, formatDrugPeriod);
    const tim = drugFieldToArabicPrint(drug.timing, timingArToEn, formatDrugTiming);
    const routeAr = drug.route ? formatDrugRoute(drug.route, 'ar') : '';
    const parts = [
      hasPrintableDrugValue(freq) ? `التكرار: ${freq}` : '',
      hasPrintableDrugValue(per) ? `المدة: ${per}` : '',
      hasPrintableDrugValue(tim) ? `التوقيت: ${tim}` : '',
      hasPrintableDrugValue(routeAr) ? `طريقة الإعطاء: ${routeAr}` : '',
    ];
    const dq = drug.doseQty != null ? String(drug.doseQty).trim() : '';
    if (hasPrintableDrugValue(dq)) parts.push(`كمية الجرعة: ${dq}`);
    const mdHyb = typeof drug.medicineDetails === 'string' ? drug.medicineDetails.trim() : '';
    if (hasPrintableDrugValue(mdHyb)) parts.push(`تفاصيل الأدوية: ${mdHyb}`);
    return joinDrugPrintParts(parts);
  };

  const getPrescriptionSettingsSource = () => {
    const useOwnerPrescriptionPrintSettings =
      canUseOwnerPrintSettings &&
      (doctorSettings.printSettings as any)?.useOwnerPrintSettings !== false &&
      !!ownerPrintSettingsSource?.printSettings;
    return useOwnerPrescriptionPrintSettings
      ? ownerPrintSettingsSource
      : doctorSettings;
  };

  const getFreshPrescriptionSettingsSource = async () => {
    const currentDoctorId = doctorId || localStorage.getItem('doctorId');
    if (!currentDoctorId) return getPrescriptionSettingsSource();

    try {
      let sourceDoctorId = currentDoctorId;
      const shouldUseOwnerSettings =
        canUseOwnerPrintSettings &&
        (doctorSettings.printSettings as any)?.useOwnerPrintSettings !== false &&
        selectedClinicId &&
        selectedClinicId !== '__all_clinics__';

      if (shouldUseOwnerSettings) {
        const ownerDoctorId = await getClinicOwnerDoctorId(selectedClinicId, currentDoctorId);
        if (ownerDoctorId && ownerDoctorId !== currentDoctorId) {
          sourceDoctorId = ownerDoctorId;
        }
      }

      const response = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SETTINGS(sourceDoctorId)}`);
      return response.data?.settings || getPrescriptionSettingsSource();
    } catch (error) {
      console.warn('[DashboardPrint] failed to refresh prescription settings:', error);
      return getPrescriptionSettingsSource();
    }
  };

  const buildReceiptPrintHtml = (receipt: Receipt): string => {
    console.log('Building receipt print HTML:', receipt);

    const prescriptionSettingsSource = getPrescriptionSettingsSource();
    const printSettings = prescriptionSettingsSource.printSettings || {
      paperSize: 'a4',
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
      showHeader: true,
      showFooter: true,
      showPatientInfo: true
    };

    // Printed labels follow the dashboard language toggle, not doctorSettings.printSettings.printLocale (often stuck to 'ar' in DB).
    const uiLang =
      (typeof window !== 'undefined' && (localStorage.getItem('language') as 'en' | 'ar' | null)) ||
      language;
    const printLocale = uiLang === 'ar' ? 'ar' : 'en';
    /** English prescription body + Arabic labels for patient line & raw frequency values (local setting). */
    const hybridArPatientFreqOnEn = getRxPrintArPatientFreqOnEnPrescription() && printLocale === 'en';

    const prescriptionVisits: any[] = (selectedPatient as any)?.visits || [];
    const prescriptionOwningVisit = prescriptionVisits.find((v: any) =>
      (v.receipts || []).some((r: any) => r._id === (receipt as any)._id)
    );

    // Most recent non-empty diagnosis from patient history
    // (complaint_history entries + visits with a diagnosis), sorted by date desc.
    const escapeHtml = (s: string) =>
      String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    const complaintHistoryRows: any[] = ((selectedPatient as any)?.complaint_history || []).map((e: any) => ({
      date: e.date,
      diagnosis: e.diagnosis || '',
    }));
    const visitDiagnosisRows: any[] = prescriptionVisits
      .filter((v: any) => v && (v.complaint || v.diagnosis))
      .map((v: any) => ({
        date: v.date,
        diagnosis: v.diagnosis || '',
      }));
    const combinedDiagnosisRows = [...complaintHistoryRows, ...visitDiagnosisRows].sort(
      (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()
    );
    let latestVisitDiagnosis = '';
    for (const row of combinedDiagnosisRows) {
      const t = row.diagnosis && String(row.diagnosis).trim();
      if (t) { latestVisitDiagnosis = t; break; }
    }
    const lastDiagnosisLabel = printLocale === 'ar' ? 'آخر تشخيص' : 'Last diagnosis';
    const lastDiagnosisHtml = latestVisitDiagnosis
      ? `<div class="last-diagnosis"><span class="ld-label">${lastDiagnosisLabel}:</span> <span class="ld-value">${escapeHtml(latestVisitDiagnosis)}</span></div>`
      : '';
    const obgynComplaintHtml =
      (receipt as any).showComplaint !== false && prescriptionOwningVisit?.complaint
        ? `<div class="last-diagnosis"><span class="ld-label">${printLocale === 'ar' ? 'الشكوى' : 'Complaint'}:</span> <span class="ld-value">${escapeHtml(prescriptionOwningVisit.complaint)}</span></div>`
        : '';
    const obgynDiagnosisHtml = (receipt as any).showDiagnosis !== false ? lastDiagnosisHtml : '';
    const nextVisitDateHtml = (receipt as any).nextVisitDate
      ? `<div class="next-visit-date"><strong>${printLocale === 'ar' ? 'موعد الزيارة القادمة' : 'Next visit date'}:</strong> ${moment((receipt as any).nextVisitDate).format('DD-MM-YYYY')}</div>`
      : '';
    const prescriptionRawVisitType =
      prescriptionOwningVisit?.visit_type || (selectedPatient as any)?.visit_type || '';
    const prescriptionVtMatch = visitTypes.find(
      (vt: any) =>
        vt.type_id === prescriptionRawVisitType ||
        vt.name === prescriptionRawVisitType ||
        vt.name_ar === prescriptionRawVisitType
    );
    const prescriptionVisitLabelAr = prescriptionVtMatch
      ? (prescriptionVtMatch.name_ar || prescriptionVtMatch.name || prescriptionRawVisitType)
      : prescriptionRawVisitType;
    const prescriptionVisitLabelEn = prescriptionVtMatch
      ? (prescriptionVtMatch.name || prescriptionVtMatch.name_ar || prescriptionRawVisitType)
      : prescriptionRawVisitType;

    const isCustomPaper = !printSettings.showHeader;
    const rxHeader = printSettings.prescriptionHeader;
    const rxFooter = printSettings.prescriptionFooter;
    const prescriptionCustomCss = String((printSettings as any).prescriptionCustomCss || '').replace(/<\/?style[^>]*>/gi, '');
    const rxHeaderHeight = Math.max(0, Number((rxHeader as any)?.headerHeight || 0));
    const rxLogoWidth = Math.max(1, Number((rxHeader as any)?.centerBadgeWidth || (rxHeader as any)?.centerBadgeSize || 80));
    const rxLogoHeight = Math.max(0, Number((rxHeader as any)?.centerBadgeHeight || 0));
    const isObgynTemplate =
      (receipt as any).prescriptionTemplate === 'obgyn' ||
      (receipt as any).prescriptionTemplate === 'infertility';
    const isPhysicianTemplate = (receipt as any).prescriptionTemplate === 'physician';
    const legacyPhysicianOrientation = localStorage.getItem(`physicianPrintOrientation_${doctorId || 'default'}`);
    const physicianOrientation = (printSettings as any).physicianPrintOrientation === 'portrait' ||
      (!(printSettings as any).physicianPrintOrientation && legacyPhysicianOrientation === 'portrait')
      ? 'portrait'
      : 'landscape';
    const obgynTopSpacerMm = Math.max(0, Math.round(((printSettings.marginTop || 0) / 2) * 0.55));
    const physicianDiagnosis = escapeHtml((receipt as any).diagnosis || latestVisitDiagnosis || '');
    const physicianProgram = escapeHtml(receipt.notes || '').replace(/\r?\n/g, '<br />');
    const physicianPaymentLabel = (selectedPatient as any)?.payment_type === 'insurance'
      ? ((selectedPatient as any)?.insurance_company_name || 'تأمين')
      : 'كاش';
    const physicianSessionRows = Array.from({ length: 12 }, (_, index) => `
      <tr>
        <td>&nbsp;</td>
        <td class="pt-number">${index + 1}</td>
        <td>&nbsp;</td>
        <td>&nbsp;</td>
        <td>&nbsp;</td>
      </tr>`).join('');
    const physicianBodyHtml = `
      <section class="physician-sheet">
        <div class="physician-red-rule"></div>
        <div class="physician-name-date">
          <div><strong>Name:</strong><span class="physician-ar">الاسم:</span><span class="physician-line-value">${escapeHtml(selectedPatient?.patient_name || '')}</span></div>
          <div><strong>Mobile:</strong><span class="physician-ar">الموبايل:</span><span class="physician-line-value">${escapeHtml(selectedPatient?.patient_phone || '')}</span></div>
          <div class="physician-insurance"><strong>Insurance:</strong><span class="physician-ar">شركة التأمين:</span><span class="physician-line-value">${escapeHtml(physicianPaymentLabel)}</span></div>
        </div>
        <div class="physician-main${physicianOrientation === 'landscape' ? ' physician-main--landscape' : ''}">
          <div class="physician-clinical-fields">
            <div class="physician-field physician-diagnosis">
              <div class="physician-label"><strong>Diagnosis:</strong><span class="physician-ar">التشخيص:</span></div>
              <div class="physician-writing-area">${physicianDiagnosis}</div>
            </div>
            <div class="physician-field physician-program">
              <div class="physician-label"><strong>Program:</strong><span class="physician-ar">البرنامج:</span></div>
              <div class="physician-writing-area">${physicianProgram}</div>
            </div>
          </div>
          <table class="physician-session-table">
            <thead><tr>
              <th>التاريخ</th>
              <th class="pt-number">المسلسل</th>
              <th>Date</th>
              <th>&#1575;&#1604;&#1578;&#1608;&#1602;&#1610;&#1593;</th>
              <th>الملاحظات</th>
            </tr></thead>
            <tbody>${physicianSessionRows}</tbody>
          </table>
        </div>
      </section>`;

    const printHtml = `
      <html>
        <head>
          <title>Patient Receipt</title>
          <style>
            @page {
              size: ${isPhysicianTemplate
                ? `${printSettings.paperSize === 'custom' ? 'A4' : printSettings.paperSize} ${physicianOrientation}`
                : (printSettings.paperSize === 'custom' ? 'auto' : printSettings.paperSize)};
              margin: 0;
            }
            html, body { 
              font-family: Arial, sans-serif; 
              direction: ${printLocale === 'ar' ? 'rtl' : 'ltr'}; 
              margin: 0; 
              padding: 0;
            }
              .top-spacer {
                display: block;
                height: ${((printSettings.marginTop || 0) / 2)}mm;
                width: 100%;
              }
              .receipt { 
                padding-top: 0;
                padding-left: calc(${((printSettings.marginLeft || 0) / 2)}mm + 10px);
                padding-right: calc(${((printSettings.marginRight || 0) / 2)}mm + 10px);
                padding-bottom: 0;
                box-sizing: border-box;
              }
            /* OB/GYN print: near full width (minimal side inset vs default receipt) */
            .receipt.receipt--obgyn {
              width: 100%;
              max-width: 100%;
              padding-left: calc(${((printSettings.marginLeft || 0) / 2)}mm + 4px);
              padding-right: calc(${((printSettings.marginRight || 0) / 2)}mm + 4px);
              box-sizing: border-box;
            }
            .top-spacer.top-spacer--obgyn { height: ${obgynTopSpacerMm}mm; }
            .rx-header.rx-header--obgyn { padding: 6px 10px; }
            .header { 
              text-align: center; 
              margin-bottom: 20px; 
              border-bottom: ${isCustomPaper ? 'none' : '1px solid #ccc'}; 
              padding-bottom: 15px; 
              display: ${isCustomPaper ? 'none' : 'block'};
            }
            .clinic-info { margin-bottom: 15px; }
            .compact-print-patient {
              margin-bottom: 8px;
              padding: 4px 6px;
            }
            .compact-print-patient .pps-name {
              font-size: 14px;
              font-weight: 600;
              margin: 0 0 3px 0;
              line-height: 1.25;
            }
            .patient-info { 
              margin-bottom: 10px; 
              padding: 4px 6px; 
              background-color: ${isCustomPaper ? 'transparent' : '#f8f8f8'}; 
              border-radius: 3px; 
              font-size: 13px;
              line-height: 1.35;
              display: ${printSettings.showPatientInfo !== false ? 'block' : 'none'};
            }
            .patient-info .pps-meta-line { margin: 0; padding: 0; }
            .patient-info .pps-sep { margin: 0 5px; color: #888; }
            .drug-item { 
              margin-bottom: 10px; 
              padding: 5px; 
              border-bottom: 1px solid #eee;
            }
            .notes { 
              margin-top: 20px; 
              padding: 10px; 
              background-color: ${isCustomPaper ? 'transparent' : '#f5f5f5'}; 
              border-radius: 5px; 
            }
            .footer { 
              margin-top: 30px; 
              border-top: ${isCustomPaper ? 'none' : '1px solid #ccc'}; 
              padding-top: 12px;
              text-align: center; 
              font-style: italic; 
              font-size: 14px;
              line-height: 1.55;
              white-space: normal;
              display: ${printSettings.showFooter ? 'block' : 'none'};
            }
            h1, h2, h3 { margin: 5px 0; }
            .rx-header { display: flex; align-items: flex-start; justify-content: space-between; padding: 8px 15px 10px; border-bottom: 0; }
            .rx-header .rx-col { flex: 1; }
            .rx-header .rx-col-left { text-align: left; }
            .rx-header .rx-col-right { text-align: right; direction: rtl; }
            .rx-header .rx-col-center { flex: 0 0 auto; padding: 0 12px; text-align: center; }
            .rx-header .rx-line { margin: 1px 0; line-height: 1.35; }
            .rx-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; border-top: 0; padding: 10px 22px 12px; box-sizing: border-box; }
            .rx-footer .rx-fline { margin: 2px 0; line-height: 1.55; direction: rtl; white-space: normal; }
            /* OB/GYN two-column layout (compact vertical rhythm) */
            .obgyn-layout { display: flex; gap: 6px; margin-bottom: 6px; }
            .obgyn-left { flex: 0 0 28%; border: 1.5px solid #d63384; border-radius: 4px; padding: 5px 6px; }
            .obgyn-right { flex: 1; border: 1.5px solid #d63384; border-radius: 4px; padding: 5px 6px; }
            .obgyn-box-title { font-size: 14px; font-weight: bold; color: #d63384; margin-bottom: 3px; border-bottom: 1px solid #f0c; padding-bottom: 2px; line-height: 1.2; }
            .oh-table { width: 100%; border-collapse: collapse; font-size: 13px; }
            .oh-table tr td { padding: 1px 2px; vertical-align: top; line-height: 1.25; }
            .oh-table .oh-label { color: #555; font-weight: bold; white-space: nowrap; padding-right: 4px; }
            /* Investigations section */
            .inv-section { margin-top: 6px; border: 1.5px solid #d63384; border-radius: 4px; padding: 5px 6px; }
            .inv-title { font-size: 14px; font-weight: bold; color: #d63384; margin-bottom: 3px; line-height: 1.2; }
            .inv-grid { display: flex; flex-wrap: wrap; gap: 2px 8px; }
            .inv-item { font-size: 13px; white-space: nowrap; }
            .inv-checked { font-weight: bold; color: #000; }
            .inv-item:not(.inv-checked) { color: #999; }
            /* OB/GYN patient header line */
            .obgyn-patient-header { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; font-size: 14px; margin-bottom: 4px; padding: 2px 5px; background: #fff8fb; border: 1px solid #f0c; border-radius: 3px; line-height: 1.25; }
            .oph-sep { color: #bbb; }
            .oph-name { font-size: 14px; }
            /* Compact drug items for OB/GYN right column */
            .obgyn-right .drug-item { margin-bottom: 3px; padding: 2px 2px; border-bottom: 1px solid #f8d; }
            .obgyn-right .drug-item h4 { margin: 0; font-size: 14px; line-height: 1.2; }
            .obgyn-right .drug-item p { margin: 0; font-size: 13px; color: #444; line-height: 1.25; }
            .obgyn-notes-inline { margin-top: 4px; padding-top: 4px; border-top: 1px solid #f8d; }
            .obgyn-notes-inline h3 { margin: 0 0 2px 0; font-size: 14px; line-height: 1.2; }
            .obgyn-notes-inline p { margin: 0; font-size: 13px; white-space: pre-wrap; line-height: 1.25; }
            /* Last diagnosis caption shown inside the Medications block */
            .last-diagnosis {
              margin: 0 0 6px 0;
              padding: 4px 8px;
              background: #f6ffed;
              border: 1px solid #b7eb8f;
              border-radius: 4px;
              font-size: 13px;
              line-height: 1.3;
              white-space: pre-wrap;
              word-wrap: break-word;
            }
            .last-diagnosis .ld-label { font-weight: bold; color: #389e0d; margin-right: 4px; }
            .last-diagnosis .ld-value { color: #222; }
            .drugs .last-diagnosis { margin-top: 4px; }
            .next-visit-date {
              margin: 10px 0 4px;
              padding: 6px 10px;
              border: 1px solid #91caff;
              border-radius: 4px;
              background: #e6f4ff;
              color: #0958d9;
              font-size: 13px;
              text-align: center;
            }
            /* Physician / physical-therapy follow-up sheet */
            .receipt.receipt--physician { padding-left: 12mm; padding-right: 12mm; }
            .physician-sheet { color: #122a72; direction: ltr; padding-top: 2px; }
            .physician-red-rule { border-top: 1.5px solid #e21f2f; margin: 0 0 10px; }
            .physician-name-date { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 12px; font-size: 11px; }
            .physician-name-date > div { display: flex; align-items: center; gap: 6px; border-bottom: 1px solid #aaa; min-height: 22px; }
            .physician-name-date .physician-insurance { grid-column: 1 / -1; }
            .physician-ar { direction: rtl; unicode-bidi: isolate; margin-left: 5px; }
            .physician-line-value { color: #111; font-size: 12px; flex: 1; padding: 0 4px; }
            .physician-field { margin-bottom: 12px; }
            .physician-label { display: flex; gap: 18px; align-items: center; font-size: 11px; margin-bottom: 2px; }
            .physician-writing-area { color: #111; min-height: 60px; line-height: 20px; padding: 0 4px; white-space: normal; }
            .physician-program .physician-writing-area { min-height: 60px; }
            .physician-main--landscape { display: flex; align-items: stretch; gap: 10mm; direction: ltr; }
            .physician-main--landscape .physician-clinical-fields { flex: 0 0 38%; min-width: 0; padding-top: 14px; }
            .physician-main--landscape .physician-session-table { flex: 1 1 62%; width: auto; }
            .physician-main--landscape .physician-writing-area { min-height: 36mm; }
            .physician-main--landscape .physician-program .physician-writing-area { min-height: 44mm; }
            .physician-session-table { width: 100%; table-layout: fixed; border-collapse: collapse; margin-top: 14px; border: 1px solid #17367f; direction: rtl; }
            .physician-session-table th { background: #172f72; color: #fff; height: 26px; padding: 3px 5px; font-size: 10px; font-weight: 600; text-align: center; border-right: 1px solid #fff; direction: rtl; }
            .physician-session-table th:last-child { border-right: 0; }
            .physician-session-table { break-inside: avoid; page-break-inside: avoid; }
            .physician-session-table td { height: ${physicianOrientation === 'landscape' ? '8mm' : '13.5mm'}; padding: 3px 5px; color: #122a72; border-right: 1px solid #c5c8ce; }
            .physician-session-table tbody td:nth-child(4),
            .physician-session-table tbody td:nth-child(5) { border-bottom: 1px solid #c5c8ce; }
            .physician-session-table tbody tr:last-child td:nth-child(4),
            .physician-session-table tbody tr:last-child td:nth-child(5) { border-bottom: 0; }
            .physician-session-table tbody tr:nth-child(even) td { background: #f0f1f4; }
            .physician-session-table .pt-number { width: 12%; text-align: center; font-weight: 700; }
            .physician-session-table th:nth-child(1) { width: 19%; }
            .physician-session-table th:nth-child(2) { width: 12%; }
            .physician-session-table th:nth-child(3) { width: 19%; }
            .physician-session-table th:nth-child(4) { width: 16%; }
            .physician-session-table th:nth-child(5) { width: 34%; }
            ${prescriptionCustomCss}
          </style>
        </head>
        <body>
          <div class="top-spacer${isObgynTemplate ? ' top-spacer--obgyn' : ''}"></div>
          ${rxHeader?.enabled ? `
            <div class="rx-header${isObgynTemplate ? ' rx-header--obgyn' : ''}" style="${rxHeaderHeight ? `min-height:${rxHeaderHeight}px;` : ''}">
              <div class="rx-col rx-col-left">
                ${(rxHeader.leftLines || []).map((l: any, index: number) => {
                  const text = typeof l === 'string' ? l : (l.text || '');
                  const fs = typeof l === 'object' ? (l.fontSize || 14) : 14;
                  const isTitleLine = index === 0;
                  const displayFs = isTitleLine ? Math.max(fs, 24) : fs;
                  const fw = isTitleLine || (typeof l === 'object' && l.bold) ? 'bold' : 'normal';
                  return text.trim() ? `<div class="rx-line" style="font-size:${displayFs}px;font-weight:${fw}">${text}</div>` : '';
                }).join('')}
              </div>
              ${rxHeader.centerBadgeBase64 ? `
                <div class="rx-col-center"><img src="${rxHeader.centerBadgeBase64}" style="width:${rxLogoWidth}px;height:${rxLogoHeight ? `${rxLogoHeight}px` : 'auto'};object-fit:contain" /></div>
              ` : ''}
              <div class="rx-col rx-col-right">
                ${(rxHeader.rightLines || []).map((l: any, index: number) => {
                  const text = typeof l === 'string' ? l : (l.text || '');
                  const fs = typeof l === 'object' ? (l.fontSize || 14) : 14;
                  const isTitleLine = index === 0;
                  const displayFs = isTitleLine ? Math.max(fs, 24) : fs;
                  const fw = isTitleLine || (typeof l === 'object' && l.bold) ? 'bold' : 'normal';
                  return text.trim() ? `<div class="rx-line" style="font-size:${displayFs}px;font-weight:${fw}">${text}</div>` : '';
                }).join('')}
              </div>
            </div>
          ` : ''}
          <div class="receipt${isObgynTemplate ? ' receipt--obgyn' : ''}${isPhysicianTemplate ? ' receipt--physician' : ''}">
            ${!rxHeader?.enabled && prescriptionSettingsSource.receiptHeader ? `
              <div class="header">
                <div class="custom-header">${prescriptionSettingsSource.receiptHeader}</div>
              </div>
            ` : ''}
            ${!isObgynTemplate && !isPhysicianTemplate ? `
            <div class="compact-print-patient">
              <div class="pps-name">${hybridArPatientFreqOnEn
        ? `اسم المريض: ${selectedPatient?.patient_name || ''}`
        : printLocale === 'ar'
        ? `اسم المريض: ${selectedPatient?.patient_name || ''}`
        : `Patient: ${selectedPatient?.patient_name || ''}`
      }</div>
              <div class="patient-info">
                <p class="pps-meta-line">${hybridArPatientFreqOnEn
        ? `<span>العمر: ${selectedPatient?.age ?? ''}</span><span class="pps-sep">·</span>${(selectedPatient as any)?.address ? `<span>العنوان: ${(selectedPatient as any).address}</span><span class="pps-sep">·</span>` : ''}${prescriptionVisitLabelAr ? `<span>نوع الزيارة: ${prescriptionVisitLabelAr}</span><span class="pps-sep">·</span>` : ''}<span>تاريخ: ${moment(receipt.date).format('DD-MM-YYYY')}</span>`
        : printLocale === 'ar'
        ? `<span>العمر: ${selectedPatient?.age ?? ''}</span><span class="pps-sep">·</span><span>تاريخ: ${moment(receipt.date).format('DD-MM-YYYY')}</span>`
        : `<span>Age: ${selectedPatient?.age ?? ''}</span><span class="pps-sep">·</span><span>Date: ${moment(receipt.date).format('DD-MM-YYYY')}</span>`
      }</p>
              </div>
            </div>` : ''}
            ${isPhysicianTemplate ? physicianBodyHtml : ((receipt as any).prescriptionTemplate === 'obgyn' || (receipt as any).prescriptionTemplate === 'infertility') ? (() => {
      const locale = printLocale;
      const h = (selectedPatient as any)?.obgynHistory || {};

      const gplad = [
        h.gravidity != null ? `G${h.gravidity}` : '',
        h.parity != null ? `P${h.parity}` : '',
        h.living != null ? `L${h.living}` : '',
        h.abortion != null ? `A${h.abortion}` : '',
        h.deaths != null ? `D${h.deaths}` : '',
      ].filter(Boolean).join(' ');
      const fetalAssessment = (receipt as any).fetalAssessment || {};
      const fetalAssessmentHtml = ([
        ['EFW', fetalAssessment.efw],
        ['Fetal pulse', fetalAssessment.fetalPulse],
        ['ES', fetalAssessment.es],
        ['AS', fetalAssessment.as],
        ['Gender', fetalAssessment.gender],
        ['Liquor', fetalAssessment.liquor],
      ] as [string, string][]).filter(([, value]) => String(value || '').trim()).map(([label, value]) =>
        `<span class="inv-item inv-checked"><strong>${label}:</strong> ${escapeHtml(value)}</span>`
      ).join('');
      const obgynRows = [
        h.marriedSince ? `<tr><td class="oh-label">${locale === 'ar' ? 'متزوجة منذ' : 'Married since'}:</td><td>${h.marriedSince}</td></tr>` : '',
        gplad ? `<tr><td class="oh-label">GPLAD:</td><td>${gplad}</td></tr>` : '',
        h.deliveryType ? `<tr><td class="oh-label">${locale === 'ar' ? 'نوع الولادة' : 'Delivery'}:</td><td>${h.deliveryType}${h.deliverySince ? ' - ' + h.deliverySince : ''}</td></tr>` : '',
        h.lmp ? `<tr><td class="oh-label">LMP:</td><td>${h.lmp}</td></tr>` : '',
        h.edd ? `<tr><td class="oh-label">EDD:</td><td>${h.edd}</td></tr>` : '',
        h.medications ? `<tr><td class="oh-label">${locale === 'ar' ? 'سجل الأدوية' : 'Medications History'}:</td><td>${h.medications}</td></tr>` : '',
        h.pastHistory ? `<tr><td class="oh-label">${locale === 'ar' ? 'التاريخ المرضي' : 'Past history'}:</td><td>${h.pastHistory}</td></tr>` : '',
        h.surgicalHistory ? `<tr><td class="oh-label">${locale === 'ar' ? 'التاريخ الجراحي' : 'Surgical history'}:</td><td>${h.surgicalHistory}</td></tr>` : '',
        h.husbandName ? `<tr><td class="oh-label">${locale === 'ar' ? 'اسم الزوج' : 'Husband name'}:</td><td>${h.husbandName}</td></tr>` : '',
        h.husbandAge ? `<tr><td class="oh-label">${locale === 'ar' ? 'عمر الزوج' : 'Husband age'}:</td><td>${h.husbandAge}</td></tr>` : '',
        h.husbandJob ? `<tr><td class="oh-label">${locale === 'ar' ? 'مهنة الزوج' : 'Husband job'}:</td><td>${h.husbandJob}</td></tr>` : '',
        h.husbandResidency ? `<tr><td class="oh-label">${locale === 'ar' ? 'الإقامة' : 'Residency'}:</td><td>${h.husbandResidency}</td></tr>` : '',
        h.familyHistory ? `<tr><td class="oh-label">${locale === 'ar' ? 'التاريخ العائلي' : 'Family history'}:</td><td>${h.familyHistory}</td></tr>` : '',
        h.consanguinity ? `<tr><td class="oh-label">${locale === 'ar' ? 'زواج أقارب' : 'Consanguinity'}:</td><td>${h.consanguinity === 'yes' ? (locale === 'ar' ? 'نعم' : 'Yes') : (locale === 'ar' ? 'لا' : 'No')}</td></tr>` : '',
        h.bloodGroup ? `<tr><td class="oh-label">${locale === 'ar' ? 'فصيلة الدم' : 'Blood group'}:</td><td>${h.bloodGroup}</td></tr>` : '',
        h.bloodTransfusion ? `<tr><td class="oh-label">${locale === 'ar' ? 'نقل الدم' : 'Blood transfusion'}:</td><td>${h.bloodTransfusion === 'yes' ? (locale === 'ar' ? 'نعم' : 'Yes') : (locale === 'ar' ? 'لا' : 'No')}</td></tr>` : '',
        h.bloodPressure ? `<tr><td class="oh-label">${locale === 'ar' ? 'ضغط الدم' : 'Blood pressure'}:</td><td>${h.bloodPressure}</td></tr>` : '',
        fetalAssessmentHtml ? `<tr><td class="oh-label">${locale === 'ar' ? 'تقييم الجنين' : 'Fetal assessment'}:</td><td><div class="inv-grid">${fetalAssessmentHtml}</div></td></tr>` : '',
        h.weight ? `<tr><td class="oh-label">${locale === 'ar' ? 'الوزن' : 'Weight'}:</td><td>${h.weight}</td></tr>` : '',
        h.temperature ? `<tr><td class="oh-label">${locale === 'ar' ? 'درجة الحرارة' : 'Temperature'}:</td><td>${h.temperature}</td></tr>` : '',
        h.semen ? `<tr><td class="oh-label">Semen:</td><td>${h.semen}</td></tr>` : '',
        h.hormones ? `<tr><td class="oh-label">Hormones:</td><td>${h.hormones}</td></tr>` : '',
        h.hsg ? `<tr><td class="oh-label">HSG:</td><td>${h.hsg}</td></tr>` : '',
      ].filter(Boolean).join('');

      const drugsHtml = receipt.drugs.length > 0 ? receipt.drugs.map((drug: any, index: number) => {
        const formLabel  = drug.form  ? formatDrugForm(drug.form, locale)   : '';
        const routeLabel = drug.route ? formatDrugRoute(drug.route, locale) : '';
        const drugFreeText = splitDrugFreeTextForPrint(drug.drug);
        const printedDrugName = drugFreeText.title;
        const drugTitle  = formLabel ? `${printedDrugName} (${formLabel})` : printedDrugName;
        let detailsLine = '';
        if (locale === 'ar') {
          const detailsParts = [
            hasPrintableDrugValue(drug.frequency) ? `التكرار: ${drug.frequency}` : '',
            hasPrintableDrugValue(drug.period) ? `المدة: ${drug.period}` : '',
            hasPrintableDrugValue(drug.timing) ? `التوقيت: ${drug.timing}` : '',
            hasPrintableDrugValue(routeLabel) ? `طريقة الإعطاء: ${routeLabel}` : '',
          ];
          const dqAr = drug.doseQty != null ? String(drug.doseQty).trim() : '';
          if (hasPrintableDrugValue(dqAr)) detailsParts.push(`كمية الجرعة: ${dqAr}`);
          const mdAr = typeof drug.medicineDetails === 'string' ? drug.medicineDetails.trim() : '';
          if (hasPrintableDrugValue(mdAr)) detailsParts.push(`تفاصيل الأدوية: ${mdAr}`);
          detailsLine = joinDrugPrintParts(detailsParts);
        } else if (hybridArPatientFreqOnEn) {
          detailsLine = hybridArabicDrugDetailsLine(drug);
        } else {
          const detailsParts = [
            hasPrintableDrugValue(drug.frequency) ? `Frequency: ${formatDrugFrequency(drug.frequency)}` : '',
            hasPrintableDrugValue(drug.period) ? `Duration: ${formatDrugPeriod(drug.period)}` : '',
            hasPrintableDrugValue(drug.timing) ? `Timing: ${formatDrugTiming(drug.timing)}` : '',
            hasPrintableDrugValue(routeLabel) ? `Route: ${routeLabel}` : '',
          ];
          const dqEn = drug.doseQty != null ? String(drug.doseQty).trim() : '';
          if (hasPrintableDrugValue(dqEn)) detailsParts.push(`Dose: ${formatDrugDoseQty(dqEn)}`);
          const mdEn = typeof drug.medicineDetails === 'string' ? drug.medicineDetails.trim() : '';
          if (hasPrintableDrugValue(mdEn)) detailsParts.push(`Medicine details: ${mdEn}`);
          detailsLine = joinDrugPrintParts(detailsParts);
        }
        const descriptionHtml = [drugFreeText.descriptionHtml, detailsLine].filter(Boolean).join('<br/>');
        return `<div class="drug-item"><h4>${index + 1}. ${drugTitle}</h4>${descriptionHtml ? `<p>${descriptionHtml}</p>` : ''}</div>`;
      }).join('') : '';

      const selected: string[] = (receipt as any).investigations || [];
      // Print only selected investigations (includes custom ones added by the doctor)
      const invHtml = selected.map(inv =>
        `<span class="inv-item inv-checked">&#9746; ${inv}</span>`
      ).join('');
      // One-line patient header for OB/GYN (always Arabic labels + RTL for hybrid print)
      const patientAddress = (selectedPatient as any)?.address || '';
      const obgynPatientHeader = `
        <div class="obgyn-patient-header" dir="rtl">
          <span class="oph-name">اسم المريضة: <strong>${selectedPatient?.patient_name || ''}</strong></span>
          <span class="oph-sep">·</span>
          <span>العمر: ${selectedPatient?.age ?? ''}</span>
          ${patientAddress ? `<span class="oph-sep">·</span><span>العنوان: ${patientAddress}</span>` : ''}
          ${prescriptionVisitLabelAr ? `<span class="oph-sep">·</span><span>نوع الزيارة: <strong>${prescriptionVisitLabelAr}</strong></span>` : ''}
          <span class="oph-sep">·</span>
          <span>تاريخ: ${moment(receipt.date).format('DD-MM-YYYY')}</span>
        </div>`;

      return `
        ${obgynPatientHeader}
        <div class="obgyn-layout">
          <div class="obgyn-left">
            <div class="obgyn-box-title">${locale === 'ar' ? 'التاريخ المرضي' : 'history'}</div>
            <table class="oh-table">${obgynRows}</table>
            ${receipt.notes ? `<div class="notes obgyn-notes-inline"><h3>${locale === 'ar' ? 'ملاحظات:' : 'Notes:'}</h3><p>${receipt.notes}</p></div>` : ''}
          </div>
          <div class="obgyn-right">
            <div class="obgyn-box-title">${locale === 'ar' ? 'الأدوية' : 'Medications'}</div>
            ${drugsHtml || '<p style="color:#aaa;font-size:14px">&nbsp;</p>'}
          </div>
        </div>
        ${selected.length > 0 ? `
        <div class="inv-section">
          <div class="inv-title">${locale === 'ar' ? 'الفحوصات' : 'Investigations'}</div>
          <div class="inv-grid">${invHtml}</div>
        </div>` : ''}
        ${obgynComplaintHtml}${obgynDiagnosisHtml}
      `;
    })() : `
            <div class="drugs">
              <h3>${printLocale === 'ar' ? 'الأدوية:' : 'Medications:'}</h3>
              ${lastDiagnosisHtml}
              ${receipt.drugs.map((drug: any, index: number) => {
        const locale = printLocale;
        const formLabel  = drug.form  ? formatDrugForm(drug.form, locale)   : '';
        const routeLabel = drug.route ? formatDrugRoute(drug.route, locale) : '';
        const drugFreeText = splitDrugFreeTextForPrint(drug.drug);
        const printedDrugName = drugFreeText.title;
        const drugTitle  = formLabel ? `${printedDrugName} (${formLabel})` : printedDrugName;
        let detailsLine = '';
        if (locale === 'ar') {
          const detailsParts = [
            hasPrintableDrugValue(drug.frequency) ? `التكرار: ${drug.frequency}` : '',
            hasPrintableDrugValue(drug.period) ? `المدة: ${drug.period}` : '',
            hasPrintableDrugValue(drug.timing) ? `التوقيت: ${drug.timing}` : '',
            hasPrintableDrugValue(routeLabel) ? `طريقة الإعطاء: ${routeLabel}` : '',
          ];
          const dqAr2 = drug.doseQty != null ? String(drug.doseQty).trim() : '';
          if (hasPrintableDrugValue(dqAr2)) detailsParts.push(`كمية الجرعة: ${dqAr2}`);
          const mdAr2 = typeof drug.medicineDetails === 'string' ? drug.medicineDetails.trim() : '';
          if (hasPrintableDrugValue(mdAr2)) detailsParts.push(`تفاصيل الأدوية: ${mdAr2}`);
          detailsLine = joinDrugPrintParts(detailsParts);
        } else if (hybridArPatientFreqOnEn) {
          detailsLine = hybridArabicDrugDetailsLine(drug);
        } else {
          const detailsParts = [
            hasPrintableDrugValue(drug.frequency) ? `Frequency: ${formatDrugFrequency(drug.frequency)}` : '',
            hasPrintableDrugValue(drug.period) ? `Duration: ${formatDrugPeriod(drug.period)}` : '',
            hasPrintableDrugValue(drug.timing) ? `Timing: ${formatDrugTiming(drug.timing)}` : '',
            hasPrintableDrugValue(routeLabel) ? `Route: ${routeLabel}` : '',
          ];
          const dqEn2 = drug.doseQty != null ? String(drug.doseQty).trim() : '';
          if (hasPrintableDrugValue(dqEn2)) detailsParts.push(`Dose: ${formatDrugDoseQty(dqEn2)}`);
          const mdEn2 = typeof drug.medicineDetails === 'string' ? drug.medicineDetails.trim() : '';
          if (hasPrintableDrugValue(mdEn2)) detailsParts.push(`Medicine details: ${mdEn2}`);
          detailsLine = joinDrugPrintParts(detailsParts);
        }
        const descriptionHtml = [drugFreeText.descriptionHtml, detailsLine].filter(Boolean).join('<br/>');
        return `
                <div class="drug-item">
                  <h4>${index + 1}. ${drugTitle}</h4>
                  ${descriptionHtml ? `<p>${descriptionHtml}</p>` : ''}
                </div>`;
      }).join('')}
            </div>
            ${receipt.notes ? `
              <div class="notes">
                <h3>${printLocale === 'ar' ? 'ملاحظات:' : 'Notes:'}</h3>
                <p>${receipt.notes}</p>
              </div>
            ` : ''}
    `}
            ${nextVisitDateHtml}
            ${printSettings.showFooter && rxFooter?.enabled && rxFooter.lines?.length > 0 ? `
              <div class="rx-footer">
                ${rxFooter.lines.map((l: any) => {
                  const text = typeof l === 'string' ? l : (l.text || '');
                  const fs = typeof l === 'object' ? (l.fontSize || 12) : 12;
                  const displayFs = Math.max(fs, 14);
                  const fw = typeof l === 'object' && l.bold ? 'bold' : 'normal';
                  return text.trim() ? `<div class="rx-fline" style="font-size:${displayFs}px;font-weight:${fw}">${text}</div>` : '';
                }).join('')}
              </div>
            ` : printSettings.showFooter && prescriptionSettingsSource.receiptFooter ? `
              <div class="footer">${prescriptionSettingsSource.receiptFooter}</div>
            ` : ''}
          </div>
        </body>
      </html>
    `;

    return printHtml;
  };

  const isPhysicalPrescriptionReceipt = (receipt: Receipt) =>
    (receipt as any)?.prescriptionTemplate === 'physician';

  const printSharedPhysicalPrescription = async (receipt: Receipt, targetWindow?: Window | null) => {
    const printWindow = targetWindow || openPrintWindow();
    if (!printWindow) return;

    const freshSettings = await getFreshPrescriptionSettingsSource();
    axios.post(`${API.BASE_URL}${API.ENDPOINTS.PHYSICAL_PRESCRIPTION_HTML}`, {
      patient: selectedPatient,
      receipt,
      settings: freshSettings,
    }).then((response) => {
      const html = response.data?.html;
      if (!response.data?.success || !html) {
        throw new Error(response.data?.message || 'Physical prescription render failed');
      }
      triggerPrint(html, printWindow);
    }).catch((error) => {
      console.error('[DashboardPrint] shared physical prescription render failed:', error);
      triggerPrint(buildReceiptPrintHtml(receipt), printWindow);
    });
  };

  const handlePrintReceipt = (receipt: Receipt) => {
    if (isPhysicalPrescriptionReceipt(receipt)) {
      printSharedPhysicalPrescription(receipt);
      return;
    }
    triggerPrint(buildReceiptPrintHtml(receipt));
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Left sidebar - Drawer on mobile, Sidebar on desktop */}
      {isMobile ? (
        <Drawer
          title="Menu"
          placement="left"
          onClose={() => setSidebarDrawerVisible(false)}
          open={sidebarDrawerVisible}
          bodyStyle={{ padding: 0 }}
          width={256}
        >
          <DashboardSidebar collapsed={false} setCollapsed={() => { }} />
        </Drawer>
      ) : (
        <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      )}

      <Layout>
        {/* Header */}
        <DashboardHeader
          onSettingsClick={handleSettingsClick}
          onMenuClick={() => isMobile && setSidebarDrawerVisible(true)}
          isMobile={isMobile}
        />

        {/* Main content and right sidebar */}
        <Layout>
          {/* Main content area */}
          <Content style={{
            margin: isMobile ? '16px 8px' : '24px 16px',
            padding: isMobile ? 12 : 24,
            background: 'white'
          }}>
            {/* Clinic Selector & Dashboard Controls - Hidden when viewing patient details */}
            {!selectedPatient && (
              <>
                {/* Clinic Selector */}
                <ClinicSelector onClinicSelect={handleClinicSelect} />

                {/* Dashboard Controls */}
                <div
                  className="dashboard-quick-toolbar"
                  style={{
                    display: 'flex',
                    alignItems: isMobile ? 'stretch' : 'center',
                    justifyContent: 'space-between',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 10,
                    marginBottom: 12,
                    padding: isMobile ? '4px 0 8px' : '4px 0 10px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                    <Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>Dr Waiting Dashboard</Title>
                    <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
                      <Button
                        type="primary"
                        className="dashboard-refresh-button"
                        onClick={handleRefresh}
                        disabled={!selectedClinicId}
                        loading={patientsNeedRefresh}
                        block={isMobile}
                      >
                        Refresh Data
                      </Button>
                      {!isViewerOnly && <Button
                        type="default"
                        onClick={() => setNotificationModalVisible(true)}
                        disabled={!selectedClinicId || !doctorId}
                        block={isMobile}
                      >
                        Send Notification
                      </Button>}
                      {!isViewerOnly && SHOW_DOCTOR_ABSENT_UI && (
                        <Button
                          type="default"
                          danger
                          onClick={() => setDoctorAbsentModalVisible(true)}
                          disabled={!doctorId}
                          block={isMobile}
                        >
                          Doctor Absent
                        </Button>
                      )}
                      {!isViewerOnly && <Button
                        icon={isMobile ? (waitingListDrawerVisible ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />) : (waitingListVisible ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />)}
                        onClick={toggleWaitingList}
                        block={isMobile}
                      >
                        {getWaitingListButtonText()}
                      </Button>}
                    </Space>
                </div>
              </>
            )}

            {/* Patient data section */}
            {selectedClinicId && !selectedPatient ? (
              <>
                <Card title="Patients List" style={{ marginBottom: 16 }}>
                  <PatientsList refreshTrigger={waitingListRefreshTrigger} />
                </Card>
                {!isViewerOnly && doctorIdForNotif && (
                  <Card title="Scheduled Visit Reminders">
                    <NextVisits />
                  </Card>
                )}
              </>
            ) : selectedPatient ? (
              <PatientDetail
                isReceiptModalVisible={isReceiptModalVisible}
                setIsReceiptModalVisible={setIsReceiptModalVisible}
                onPrintReceipt={handlePrintReceipt}
                onGetReceiptHtml={buildReceiptPrintHtml}
                onBackToList={handleBackToPatientList}
                clinicalHistoryRefreshToken={clinicalHistoryRefreshToken}
              />
            ) : (
              <>
                <Card>
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Title level={4}>Select a clinic to view patients</Title>
                  </div>
                </Card>
                {!isViewerOnly && doctorIdForNotif && (
                  <Card title="Scheduled Visit Reminders" style={{ marginTop: 16 }}>
                    <NextVisits />
                  </Card>
                )}
              </>
            )}
          </Content>

          {/* Right sidebar for waiting list - Drawer on mobile, Sidebar on desktop */}
          {!isViewerOnly && selectedClinicId && (
            <>
              {isMobile ? (
                <Drawer
                  title="Waiting List"
                  placement={isRTL ? "left" : "right"}
                  onClose={() => setWaitingListDrawerVisible(false)}
                  open={waitingListDrawerVisible}
                  width={280}
                  styles={{ body: { padding: '12px', overflowY: 'auto' } }}
                >
                  <SidebarWaitingList
                    ref={sidebarWaitingListRef}
                    refreshTrigger={waitingListRefreshTrigger}
                  />
                </Drawer>
              ) : (
                waitingListVisible && (
                  <Sider
                    width={280}
                    theme="light"
                    style={{
                      background: '#fff',
                      margin: isRTL ? '24px 0 24px 16px' : '24px 16px 24px 0',
                      borderRadius: '4px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div style={{ padding: 12, overflowY: 'auto', height: '100%' }}>
                      <SidebarWaitingList
                        ref={sidebarWaitingListRef}
                        refreshTrigger={waitingListRefreshTrigger}
                      />
                    </div>
                  </Sider>
                )
              )}
            </>
          )}
        </Layout>
      </Layout>

      {/* Receipt Modal for adding new receipt */}
      {!isViewerOnly && isReceiptModalVisible && (
        <ReceiptModal
          visible={isReceiptModalVisible}
          onCancel={() => setIsReceiptModalVisible(false)}
          patient={selectedPatient}
          onSaveAndPrint={(receipt, printWindow) => {
            if (isPhysicalPrescriptionReceipt(receipt as Receipt)) {
              printSharedPhysicalPrescription(receipt as Receipt, printWindow);
              return;
            }
            triggerPrint(buildReceiptPrintHtml(receipt as Receipt), printWindow);
          }}
          onReceiptAdded={async (updatedPatient) => {
            setIsReceiptModalVisible(false);
            if (updatedPatient) {
              setSelectedPatient((prev: any) => prev ? { ...prev, ...updatedPatient } : prev);
            }
            // PatientDetail owns the full clinic-scoped visit history. Refresh it
            // directly so the new prescription appears immediately; the patient
            // list refresh below remains for list ordering and summary data only.
            setClinicalHistoryRefreshToken((token) => token + 1);
            // Bypass FE cache so the post-write list reflects the new receipt.
            await fetchPatients({ forceRefresh: true });
          }}
        />
      )}

      {/* Modal for viewing receipt details */}
      <ReceiptDetail onPrintReceipt={handlePrintReceipt} onGetReceiptHtml={buildReceiptPrintHtml} />

      {/* Notification Modal */}
      {!isViewerOnly && <SendNotification
        visible={notificationModalVisible}
        onCancel={() => setNotificationModalVisible(false)}
        patientName={selectedPatient?.patient_name}
      />}

      {/* Doctor Absent Modal — gated by SHOW_DOCTOR_ABSENT_UI */}
      {SHOW_DOCTOR_ABSENT_UI && (
        <DoctorAbsentModal
          visible={doctorAbsentModalVisible}
          onCancel={() => setDoctorAbsentModalVisible(false)}
          doctorId={doctorId}
        />
      )}

      {/* Assistant notifications - floating dialogs at bottom-right (or bottom-left when RTL) */}
      {!isViewerOnly && <AssistantNotificationsPanel doctorId={doctorIdForNotif} isRTL={isRTL} />}
    </Layout>
  );
};

export default Dashboard;
